import React from "react";
import I18n from "../../../base/i18n.js";
import clsx from "clsx";

import { withStyles } from "@material-ui/styles";
import withCurrentUser from "../../../hocs/with_current_user.js";
import withShowToast from "../../../hocs/with_show_toast.js";
import { compose } from "redux";

import ShowMainStatisticsUseCase from "../../../use_cases/admin/statistics/main/show_main_statistics_use_case.js.jsx"
import InvestmentStatusEnum from "../../../enums/investment_status.js";
import MoneyFormatter from "../../../utils/money_formatter.js";

const styles = theme => ({
  root: {
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    overflow: "auto",
    position: "absolute",
    padding: "22px"
  },
  block: {
    boxShadow: "0px 2px 1px -1px rgba(0,0,0,0.0), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)",
    borderRadius: "4px",
    color: "#263238",
    margin: "15px 0 20px",
    overflow: "hidden"
  },
  col: {
    lineHeight: "1.7"
  },
  colSeparated: {
    marginBottom: "15px"
  },
  header: {
    backgroundColor: theme.palette.blueMain,
    color: "white",
    padding: "8px 16px"
  },
  row: {
    padding: "8px 16px"
  },
  description: {
    color: theme.palette.grayText
  }
});

class StatisticsListPage extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      statisticsGroups: []
    }
  }

  componentDidMount() {
    ShowMainStatisticsUseCase.execute()
    .then(result => {
      this.onLoadStatsSuccess(result);
    })
    .catch(result => {
      this.props.showServerErrorIfNeed(result);
    });
  }

  onLoadStatsSuccess(result) {
    const { statistics } = result.data;
    const statisticsGroups = [
      {
        title: I18n.t("admin.statistics.users.title"),
        values: [
          {
            name: I18n.t("admin.statistics.total_count"),
            value: statistics.users.totalCount
          },
          {
            name: I18n.t("admin.statistics.users.confirmed_count"),
            value: statistics.users.confirmedCount,
            level: 1
          },
          {
            name: I18n.t("admin.statistics.users.unconfirmed_count"),
            value: statistics.users.unconfirmedCount,
            level: 1,
            separate: true
          },
          {
            name: I18n.t("admin.statistics.users.with_any_paid_investment_count"),
            value: statistics.users.withAnyPaidInvestmentCount,
            description: this.paidStatusNames().join(', ')
          },
          {
            name: I18n.t("admin.statistics.users.with_all_unpaid_investments_count"),
            value: statistics.users.withAllUnpaidInvestmentsCount,
            description: this.unpaidStatusNames().join(', ')
          },
          {
            name: I18n.t("admin.statistics.users.with_all_not_completed_investments_count"),
            value: statistics.users.withAllNotCompletedInvestmentsCount,
            description: this.notCompletedStatusNames().join(', ')
          }
        ]
      },
      {
        title: I18n.t("admin.statistics.investments.title"),
        values: [
          {
            name: I18n.t("admin.statistics.investments.completed_count"),
            value: statistics.investments.completedCount,
            description: this.completedStatusNames().join(', ')
          },
          {
            name: I18n.t("admin.statistics.investments.completed_amount_sum"),
            value: this.formatAmount(statistics.investments.completedAmountSum)
          },
          {
            name: I18n.t("admin.statistics.investments.completed_median_amount"),
            value: this.formatAmount(statistics.investments.completedMedianAmount),
            separate: true
          },
          {
            name: I18n.t("admin.statistics.investments.paid_count"),
            value: statistics.investments.paidCount,
            description: this.paidStatusNames().join(', '),
            level: 1
          },
          {
            name: I18n.t("admin.statistics.investments.paid_amount_sum"),
            value: this.formatAmount(statistics.investments.paidAmountSum),
            level: 1
          },
          {
            name: I18n.t("admin.statistics.investments.paid_median_amount"),
            value: this.formatAmount(statistics.investments.paidMedianAmount),
            separate: true,
            level: 1
          },
          {
            name: I18n.t("admin.statistics.investments.unpaid_count"),
            value: statistics.investments.unpaidCount,
            description: this.unpaidStatusNames().join(', '),
            level: 1
          },
          {
            name: I18n.t("admin.statistics.investments.unpaid_amount_sum"),
            value: this.formatAmount(statistics.investments.unpaidAmountSum),
            level: 1
          },
          {
            name: I18n.t("admin.statistics.investments.unpaid_median_amount"),
            value: this.formatAmount(statistics.investments.unpaidMedianAmount),
            separate: true,
            level: 1
          },
          {
            name: I18n.t("admin.statistics.investments.not_completed_count"),
            value: statistics.investments.notCompletedCount,
            description: this.notCompletedStatusNames().join(', '),
            separate: true
          }
        ]
      },
      // {
      //   title: I18n.t("admin.statistics.subscribers.title"),
      //   values: [
      //     {
      //       name: I18n.t("admin.statistics.total_count"),
      //       value: statistics.subscribers.totalCount
      //     }
      //   ]
      // }
    ];
    this.setState({
      statisticsGroups: statisticsGroups
    });
  }

  formatAmount(amount) {
    return `${MoneyFormatter.format(amount)} ${I18n.t("currency.euro")}`
  }

  completedStatusNames() {
    return this.translateStatuses(InvestmentStatusEnum.completedValues());
  }

  notCompletedStatusNames() {
    return this.translateStatuses(InvestmentStatusEnum.notCompletedValues());
  }

  paidStatusNames() {
    return this.translateStatuses(InvestmentStatusEnum.paidValues());
  }

  unpaidStatusNames() {
    return this.translateStatuses(InvestmentStatusEnum.unpaidValues());
  }

  translateStatuses(statuses) {
    return statuses.map(value => I18n.t(`enum.investment_status.${value}`));
  }

  colClassName(value) {
    const { classes } = this.props;

    // Vertical margin to show separated blocks
    return clsx(classes.col, value.separate && classes.colSeparated);
  }

  colStyle(value) {
    // Horizontal shift to show hierarchy by level value
    return { marginLeft: `${(value.level || 0) * 15}px` };
  }

  render() {
    const { classes } = this.props;
    const { statisticsGroups } = this.state;

    return (
      <div className = { classes.root }>
        {
          statisticsGroups.map((item, idx) => (
            <div className = { classes.block }
                       key = { idx }>
              <div className = { classes.header }>
                { item.title }
              </div>
              <div className = { classes.row }>
                {
                  item.values.map((value, idx) => (
                    <div className = { this.colClassName(value) }
                             style = { this.colStyle(value) }
                               key = { idx }>
                      <b>{ value.name }:</b>
                      { ` ${value.value}` }
                      {
                        value.description &&
                        <span className = { classes.description }>
                          { ` (${value.description})` }
                        </span>
                      }
                    </div>
                  ))
                }
              </div>
            </div>
          ))
        }
      </div>
    )
  }
}

export default compose(withShowToast, withCurrentUser, withStyles(styles))(StatisticsListPage);
